import { useLocation, useMatch, useNavigate } from 'react-router';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import { ROUTES } from '../../../../../constants/RouteNameMapping';

import { createKPITemplateDataModel, IKPITemplate } from '../../../../../data-models/kpi-template.data-model';
import { ConfirmDeletionDialog } from '../../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';

import PlusIconButton from '../../../../IconButton/PlusIconButton';
import { SubSectionNavigation } from '../../SubSectionNavigation/SubSectionNavigation';
import { kpiActiveTemplatesListStateLoadableJ } from '../../../../../services/state/KPI/KPITemplatesStateJ';
import { useKPITemplateActions } from '../../../../../pages/KPI/hooks/useKPITemplateActions';
import KPITemplatesNavItems from './KPITemplatesNavItems';

export function useKPITemplatesSubNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isNewTemplate = useMatch(`/${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}/new`);

  const templateListLoadable = useAtomValue(kpiActiveTemplatesListStateLoadableJ);
  const templateList = templateListLoadable.state === 'hasData' ? templateListLoadable.data : null;
  const visibleTemplates = useMemo(
    () => templateList?.filter((t: IKPITemplate) => !t.isDeleted),
    [templateList]
  );
  const { deleteKPITemplateAction, createKPITemplateAction } = useKPITemplateActions();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const selectedTemplateId = useMemo(() => pathname.split('/')?.pop(), [pathname]);

  const handleNavigateNewTemplate = useCallback(() => {
    navigate(`/${ROUTES.KPI}/${ROUTES.KPI_TEMPLATES}/new`);
  }, [navigate]);

  const templatesListWithNewOne = useMemo(() => {
    if (!visibleTemplates) {
      return null;
    }
    const newTemplate = isNewTemplate ? createKPITemplateDataModel({ id: -1, name: 'New Template' }) : null;
    return isNewTemplate ? [newTemplate, ...visibleTemplates] : visibleTemplates;
  }, [isNewTemplate, visibleTemplates]);

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) {
      return null;
    }
    return templatesListWithNewOne?.find((t) => String(t?.id) === selectedTemplateId) ?? null;
  }, [selectedTemplateId, templatesListWithNewOne]);

  const onDuplicateTemplate = useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }

    const kpiTemplate = await createKPITemplateAction({
      ...selectedTemplate,
      name: `${selectedTemplate.name} (Copy)`,
    });

    if (!kpiTemplate?.id) {
      return;
    }

    return navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}/${kpiTemplate.id}`);
  }, [createKPITemplateAction, navigate, selectedTemplate]);

  const handleCloseDeleteModal = useCallback(() => setOpenDeleteModal(false), []);

  const handleOnDeleteTemplate = useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }
    await deleteKPITemplateAction({ ...selectedTemplate });
    handleCloseDeleteModal();

    const remaining = visibleTemplates?.filter((t) => t.id !== selectedTemplate.id) ?? [];
    if (remaining.length > 0) {
      navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}/${remaining[0].id}`);
    } else {
      // No templates left: go to the index route, which renders the empty state.
      // Do NOT navigate to /new — that would show "New Template" in the sidebar.
      navigate(`/${ROUTES.KPI_TEMPLATES_FULL_ROUTE}`);
    }
  }, [deleteKPITemplateAction, handleCloseDeleteModal, navigate, selectedTemplate, visibleTemplates]);

  return {
    templatesListWithNewOne,
    selectedTemplate,
    selectedTemplateId,
    openDeleteModal,
    setOpenDeleteModal,
    handleNavigateNewTemplate,
    handleOnDeleteTemplate,
    handleCloseDeleteModal,
    onDuplicateTemplate,
  };
}

export const KPITemplatesSubNavigation = () => {
  const { colors } = useTheme();
  const {
    templatesListWithNewOne,
    selectedTemplate,
    selectedTemplateId,
    openDeleteModal,
    setOpenDeleteModal,
    handleNavigateNewTemplate,
    handleOnDeleteTemplate,
    handleCloseDeleteModal,
    onDuplicateTemplate,
  } = useKPITemplatesSubNavigation();

  if (!templatesListWithNewOne) {
    return null;
  }

  return (
    <>
      <ConfirmDeletionDialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleOnDeleteTemplate}
        title={`Delete template "${selectedTemplate?.name}"?`}
      >
        <Typography variant='body2' color={'text.secondary'} sx={{ pt: '.25rem', minWidth: '30rem' }}>
          Deleting this template will also remove the assignment for all companies it has been attached to,
          which will cause any "To Send" and "In Progress" requests to be removed. You will still be able to
          access submitted responses.
        </Typography>
      </ConfirmDeletionDialog>
      <SubSectionNavigation
        title='Templates'
        RightSlot={
          <PlusIconButton sx={{ color: colors.secondary[20] }} onClick={handleNavigateNewTemplate}>
            Add New
          </PlusIconButton>
        }
      />
      <KPITemplatesNavItems
        selectedSection={selectedTemplateId ?? ''}
        sectionList={templatesListWithNewOne as IKPITemplate[]}
        parentRoute={ROUTES.KPI_TEMPLATES_FULL_ROUTE}
        onOpenConfirmDeleteModal={() => setOpenDeleteModal(true)}
        onDuplicateTemplate={onDuplicateTemplate}
      />
    </>
  );
};
